import { has, get } from "lodash";
import { stringify } from "query-string";
import { fetchUtils, HttpError } from "react-admin";
import axios from "axios";
import capitalize from "lodash/capitalize";
import { processStudentRecordsForCsv } from "./pages/students/tools";
import { processParentRecordsForCsv } from "./pages/parents/tools";
import { processAssignmentRecordsForCsv } from "./pages/assignments/tools";
import { AssignmentReportTypeEnum } from "./pages/assignments/const";
import { processCommercialDocumentRecordsForCsv } from "./pages/commercialDocuments/tools";

interface Query {
  pageNumber?: number;
  pageSize?: number;
  OrderType?: string;
  OrderBy?: string;
  filter?: any;
  Search?: string;
}

export const composeError = (errorBody: any) => {
  const errors: string[] = Object.values(get(errorBody, "errors", {}));
  const message = get(errorBody, "message", []);
  let msg = `${message}. \n`;
  errors.map((val: string) => (msg = msg + val + "\n"));
  return msg;
};

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const dataProvider = {
  getList: (resource: any, params: any) => {
    let query: Query = {};

    if (has(params, "pagination")) {
      const { page, perPage } = params.pagination;
      query["pageNumber"] = page;
      query["pageSize"] = perPage;
    }

    if (has(params, "sort")) {
      const { field, order } = params.sort;
      let splittedString = field.split(".");
      if (splittedString.length !== 0) {
        const capitalizedString = splittedString.map((text: string) => {
          return text.charAt(0).toUpperCase() + text.slice(1);
        });

        query["OrderBy"] = capitalizedString.join(".");
      } else {
        query["OrderBy"] = field.charAt(0).toUpperCase() + field.slice(1);
      }
      query["OrderType"] = order.toLowerCase();
    }

    if (has(params, "filter")) {
      if (params.filter.search) {
        query["Search"] = params.filter.search ?? "";

        if (resource === "parents" && params.filter.searchAllParent) {
          delete params.filter.SchoolId;
          delete params.filter.IsHaveStudent;
        }

        query["filter"] = JSON.stringify(params.filter);
      } else {
        query["filter"] = JSON.stringify(params.filter);
      }
    }

    if (resource === "enhancedBusSchedulePlans") {
      resource = "busSchedulePlans";
    }

    let url = `${apiUrl}/${resource}?${stringify(query)}`;

    return httpClient(url, getUser())
      .then(({ json, headers }: any) => {
        // if it has json.meta, then spread the meta
        const jsons = {
          data: json,
        };

        const ret = {
          json: {
            ...jsons,
            ...JSON.parse(headers.get("X-Pagination")),
          },
        };
        return ret;
      })
      .then(({ json }: any) => ({
        data: json.data,
        total: json.TotalCount,
      }));
  },

  getOne: async (resource: any, params: any) => {
    if (resource === "enhancedBusSchedulePlans") {
      resource = "busSchedulePlans";
    }

    const { json } = await httpClient(
      `${apiUrl}/${resource}/${params.id}`,
      getUser(),
    );
    return "data" in json ? json : { data: json };
  },

  getMany: (resource: any, params: any) => {
    let result;
    if (typeof params.ids[0] === "object") {
      result = params.ids.map((obj: any) => obj.id);
    } else {
      result = params.ids;
    }
    const query = {
      ids: result.toString(),
      pageNumber: 1,
      pageSize: 1000,
    };
    const url = `${apiUrl}/${resource}?${stringify(query)}`;

    return httpClient(url, getUser()).then(({ json }: any) => {
      if (resource === "customerCards") {
        return {
          data: json.values,
        };
      }
      return {
        data: json,
      };
    });
  },

  getManyReference: (resource: any, params: any) => {
    let query: Query = {};

    if (params.pagination) {
      const { page, perPage } = params.pagination;
      query["pageNumber"] = page;
      query["pageSize"] = perPage;
    }

    if (params.sort) {
      const { field, order } = params.sort;
      query["OrderBy"] = capitalize(field);
      query["OrderType"] = order.toLowerCase();
    }

    if (params.filter) {
      query["filter"] = JSON.stringify({
        ...params.filter,
        [params.target]: params.id,
      });
    }

    const url = `${apiUrl}/${resource}?${stringify(query)}`;

    return httpClient(url, getUser()).then(({ json }: any) => ({
      data: json.data,
      total: json.total,
    }));
  },

  update: (resource: any, params: any) => {
    if (params.data.tenant) {
      delete params.data.tenant;
    }

    if (resource === "enhancedBusSchedulePlans") {
      resource = "busSchedulePlans";
    }

    return httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: "PUT",
      body: JSON.stringify(params.data),
      ...getUser(),
    })
      .then(({ json }: any) => {
        if (json) {
          return "data" in json ? json : { data: json };
        }
        return {
          data: {
            id: params.id,
          },
          payload: params.data,
        };
      })
      .catch((error: any) => {
        const body = get(error, "body", {});
        const msg = composeError(body);
        return Promise.reject({ message: msg });
      });
  },

  updateMany: (resource: any, params: any) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    return httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
      method: "PUT",
      body: JSON.stringify(params.data),
      ...getUser(),
    }).then(({ json }: any) => ({ data: json }));
  },

  customUpdateMany: (resource: any, params: any) => {
    return httpClient(`${apiUrl}/${resource}`, {
      method: "PUT",
      body: JSON.stringify(params.data),
      ...getUser(),
    }).then(({ json }: any) => ({ data: json }));
  },

  create: (resource: any, params: any) => {
    if (resource === "enhancedBusSchedulePlans") {
      resource = "busSchedulePlans";
    }

    return httpClient(`${apiUrl}/${resource}`, {
      method: "POST",
      body: JSON.stringify(params.data),
      ...getUser(),
    })
      .then(({ json }: any) => {
        return {
          data: { ...params.data, id: json?.id },
          res: { json },
        };
      })
      .catch((error: any) => {
        const body = get(error, "body", {});
        const msg = composeError(body);
        return Promise.reject({ message: msg });
      });
  },

  delete: (resource: any, params: any) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: "DELETE",
      ...getUser(),
    }).then(() => {
      const data: any = { id: params.id };

      return { data };
    }),

  deleteMany: (resource: any, params: any) => {
    const ids = params.ids;
    const deleteResource = (id: number) =>
      httpClient(`${apiUrl}/${resource}/${id}`, {
        method: "DELETE",
        ...getUser(),
      })
        .then(({ body }: any) => JSON.parse(body))
        .then(({ message }: any) => (message === "Success" ? id : null));

    return Promise.all(ids.map(deleteResource)).then((ids: any) => {
      return {
        data: ids,
      };
    });
  },

  uploadImage: (file: any, progressCallback: (percent: any) => {}) => {
    let formData = new FormData();

    formData.append("file", file);

    return axios
      .request({
        method: "post",
        url: `${apiUrl}/Medias/upload`,
        data: formData,
        onUploadProgress: (progressEvent) => {
          const percentage = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          progressCallback(percentage);
        },
      })
      .then((data) => {
        return {
          data: {
            url: `${apiHost}/${data.data.relativePath}`,
          },
        };
      });
  },

  customUpdate: (resource: any, params: any) => {
    return httpClient(`${apiUrl}/${resource}`, {
      method: "PUT",
      body: JSON.stringify(params.data),
      ...getUser(),
    })
      .then(({ json }: any) => {
        if (json) {
          return "data" in json ? json : { data: json };
        }
        return {
          data: {
            id: params.id,
          },
          payload: params.data,
        };
      })
      .catch((error: any) => {
        const body = get(error, "body", {});
        const msg = composeError(body);
        return Promise.reject({ message: msg });
      });
  },

  customCreate: (resource: any, params: any) => {
    return httpClient(`${apiUrl}/${resource}`, {
      method: "POST",
      body: JSON.stringify(params.data),
      ...getUser(),
    })
      .then(({ json }: any) => {
        return {
          data: json,
        };
      })
      .catch((error: any) => {
        const body = get(error, "body", {});
        const msg = composeError(body);
        return Promise.reject({ message: msg });
      });
  },

  customPatch: (resource: any, params: any) => {
    return httpClient(`${apiUrl}/${resource}`, {
      method: "PATCH",
      body: JSON.stringify(params.data),
      ...getUser(),
    })
      .then(({ json }: any) => {
        return {
          data: json,
        };
      })
      .catch((error: any) => {
        const body = get(error, "body", {});
        const msg = composeError(body);
        return Promise.reject({ message: msg });
      });
  },

  customGetList: (resource: any, params: any) => {
    let query: Query = {};

    if (has(params, "filter")) {
      if (params.filter.search) {
        query["Search"] = params.filter.search ?? "";
        query["filter"] = JSON.stringify(params.filter);
      } else {
        query["filter"] = JSON.stringify(params.filter);
      }
    }

    if (has(params, "sort")) {
      const { field, order } = params.sort;
      let splittedString = field.split(".");
      if (splittedString.length !== 0) {
        const capitalizedString = splittedString.map((text: string) => {
          return text.charAt(0).toUpperCase() + text.slice(1);
        });

        query["OrderBy"] = capitalizedString.join(".");
      } else {
        query["OrderBy"] = field.charAt(0).toUpperCase() + field.slice(1);
      }
      query["OrderType"] = order.toLowerCase();
    }

    let url = `${apiUrl}/${resource}?${stringify(query)}`;

    return httpClient(url, getUser()).then(({ json }: any) => ({
      data: json,
    }));
  },

  customGetOne: async (resource: any) => {
    const { json } = await httpClient(`${apiUrl}/${resource}`, getUser());

    return "data" in json ? json : { data: json };
  },

  customGetListWithStaticParams: (resource: any) => {
    let url = `${apiUrl}/${resource}`;

    return httpClient(url, getUser()).then(({ json }: any) => ({
      data: json,
    }));
  },

  customDelete: (resource: any, params: any) => {
    return httpClient(`${apiUrl}/${resource}`, {
      method: "DELETE",
      ...getUser(),
    }).then(() => {
      const data: any = { id: params.id };

      return { data };
    });
  },

  getOneAxios: (resource: any, params: any) => {
    return axios
      .request({
        method: "get",
        url: `${apiUrl}/${resource}/${params.id}`,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        data: {},
      })
      .then((data) => {
        return { data: data.data };
      });
  },

  downloadFile: (resource: any) => {
    return axios
      .request({
        method: "get",
        url: `${apiUrl}/${resource}`,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        responseType: "blob",
      })
      .then((data) => {
        return { data: data.data };
      });
  },

  getRecordForCSV: async (
    resource: string,
    params: {
      filter: any;
      body: any;
      callback: (progressUpdate: {
        isLoading?: boolean;
        loadedRecordCount: number;
        totalRecords: number;
      }) => void;
    },
  ) => {
    try {
      // Update the loading state and progress using the callback
      params.callback({
        isLoading: true,
        loadedRecordCount: null,
        totalRecords: null,
      });

      // Fetch all records based on filter
      // Update the progress using the callback
      const records = await fetchAllData(resource, params.filter);

      // Stop the process if there are no records found
      if (records.length === 0) {
        return { data: [] };
      }

      params.callback({
        loadedRecordCount: 0,
        totalRecords: records.length,
      });

      let processedData = [];

      if (resource === "students") {
        processedData = await processStudentRecordsForCsv(
          params.body,
          records,
          params.callback,
        );
      }

      if (resource === "parents") {
        processedData = await processParentRecordsForCsv(
          params.body,
          records,
          params.callback,
        );
      }

      if (resource.includes("assignments")) {
        processedData = await processAssignmentRecordsForCsv(
          params.body,
          records,
          params.callback,
          params.body.reportType === AssignmentReportTypeEnum.AmBusArrivalReport
        );
      }

      if (
        resource === "busSchedulePlans" &&
        params.body.reportType === AssignmentReportTypeEnum.AssignmentSummaryReport
      ) {
        processedData = await processAssignmentRecordsForCsv(
          params.body,
          records,
          params.callback,
        );
      }

      if (resource === "commercialDocuments") {
        processedData = await processCommercialDocumentRecordsForCsv(
          params.body,
          records,
          params.callback,
        );
      }

      return { data: processedData };
    } catch (error) {
      throw new HttpError(
        "Fail to Export Records!, " + error.message,
        500,
        "Please try again in a few moments",
      );
    } finally {
      // Update the loading state and progress using the callback
      params.callback({
        isLoading: false,
        loadedRecordCount: 0,
        totalRecords: 0,
      });
    }
  },

  uploadFileToAws: async (
    presignedUrl: string,
    params: {
      data: File;
      callback: (percent: number) => void;
    },
  ) => {
    try {
      await axios.request({
        method: "put",
        url: presignedUrl,
        headers: {
          "Content-Type": params.data.type,
        },
        data: params.data,
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;
          let percent = Math.floor((loaded * 100) / (total || 1));
          params.callback(percent);
        },
      });

      const url = new URL(presignedUrl);

      return { data: `${url.origin}${url.pathname}` };
    } catch (error) {
      params.callback(0);
      const body = get(error, "body", {});
      const msg = composeError(body);
      return Promise.reject({ message: msg });
    }
  },
};

/**
 * Fetches all data for a specific resource from the API,
 * using pagination to fetch multiple pages until all data is retrieved.
 *
 * @param resource - The name of the resource to fetch (e.g., "students", "parents").
 * @param filter - (Optional) The filter criteria to apply when fetching the data.
 *                  If omitted, no filter will be applied.
 * @param sort - (Optional) Sorting options:
 *                 - `field`: The field name to sort by (case-insensitive).
 *                 - `order`: The sort direction, either "asc" or "desc".
 *                 If omitted, no sorting will be applied.
 * @returns An array containing all the fetched data.
 */
export const fetchAllData = async (
  resource: string,
  filter?: any,
  sort?: {
    field: string;
    order: "asc" | "desc";
  },
): Promise<any[]> => {
  let data: any[] = [];
  let page = 1;
  let hasNextPage = false;

  // Capitalize the first letter of the field if it exists
  const orderBy = sort?.field
    ? sort.field.charAt(0).toUpperCase() + sort.field.slice(1)
    : undefined;

  const orderType = sort?.order;

  // Loop through pages, fetching data until there are no more pages.
  do {
    try {
      const params: any = {
        PageNumber: page,
        PageSize: 100,
      };

      if (filter) {
        params.filter = JSON.stringify(filter);
      }

      if (orderBy) {
        params.OrderBy = orderBy;
      }

      if (orderType) {
        params.OrderType = orderType;
      }

      // Fetch a single page of data
      const response = await axios.get(`${apiUrl}/${resource}`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      // Add this page's data to the main data array
      data = [...data, ...response.data];

      // Check if there are more pages to fetch,
      // then move to the next page if successful
      const paginationHeaders = response.headers["x-pagination"]
        ? JSON.parse(response.headers["x-pagination"])
        : null;
      hasNextPage = !!paginationHeaders?.HasNext;
      page++;
    } catch (error) {
      throw new Error(`Failed to fetch all ${resource} data`);
    }
  } while (hasNextPage); // Continue fetching if there are more pages.

  return data;
};

export const getUser = () => {
  return {
    user: {
      authenticated: true,
      token: `Bearer ${localStorage.getItem("token")}`,
    },
  };
};

export const httpClient = fetchUtils.fetchJson;
export const apiHost = process.env.REACT_APP_API_HOST;
export const apiUrl = `${apiHost}/api`;

export default dataProvider;
